import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { AdminBar } from '@/components/admin/AdminBar';
import { breadcrumbParents, type Crumb } from '@/components/admin/breadcrumbs';
import { Sidebar } from '@/components/admin/Sidebar';
import { fetchDashboard } from '@/features/dashboard/api';
import { fetchInvitations, type Invitation } from '@/features/invitations/api';
import { fetchUnreadCount } from '@/features/notifications/api';
import { HELP_TOPICS, topicForPath } from '@/features/help/topics';

function invitationBadgeCounts(items: Invitation[] | undefined): {
  student: number;
  director: number;
  success: number;
  office: number;
  total: number;
} {
  const counts = { student: 0, director: 0, success: 0, office: 0, total: 0 };
  for (const invitation of items ?? []) {
    if (invitation.status !== 'pending') continue;
    if (invitation.role === 'student') counts.student += 1;
    else if (invitation.role === 'director') counts.director += 1;
    else if (invitation.role === 'success') counts.success += 1;
    else if (invitation.role === 'office') counts.office += 1;
  }
  counts.total = counts.student + counts.director + counts.success + counts.office;
  return counts;
}

/**
 * 全ロール共通の骨格（指示書 §7 AdminShell）。
 * アドミンバー / サイドバー / コンテンツ領域（薄グレー背景・白カード）。
 */
const SIDEBAR_COLLAPSED_KEY = 'cxo.sidebar.collapsed';

export function AdminShell({ children }: { children: ReactNode }): ReactElement {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // 畳んだ状態は次に開いたときも保つ（毎回畳み直させない / D-109）
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
  );

  function toggleCollapsed(): void {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  }

  const role = user?.active_role;
  // 理事・サクセスはシステム通知の導線を出さない（D-295）
  const showNotifications = role === 'student' || role === 'office' || role === 'super_admin';

  // 通知ベルの未読バッジ（指示書 §7: アドミンバーに通知ベル / M6）
  const unreadQuery = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: fetchUnreadCount,
    enabled: Boolean(user) && showNotifications,
    refetchInterval: 60_000,
  });

  // サイドバーの赤丸は、メッセージ系が「未読」（D-145）、イベントとフォローアップは
  // 「未対応」（D-231 / 既存の followUps）。件数の出どころはダッシュボードと同じ API。
  const badgeQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
    enabled: Boolean(user),
    refetchInterval: 60_000,
  });
  // 招待の未受諾は今日の対応には出さない（D-241）。件数は会員管理の赤丸用に別取得する
  const invitationsQuery = useQuery({
    queryKey: ['invitations', 'sidebar-badges'],
    queryFn: () => fetchInvitations(1, 100),
    enabled: user?.active_role === 'office',
    refetchInterval: 60_000,
  });
  const summary = badgeQuery.data;
  const invitationCounts = invitationBadgeCounts(invitationsQuery.data?.items);
  const badges = {
    messages: summary?.unread_messages ?? 0,
    notifications: unreadQuery.data?.unread ?? 0,
    inbox: summary?.unread_inbox_count ?? 0,
    followUps: summary?.open_follow_up_count ?? 0,
    eventTodos: summary?.event_todo_count ?? 0,
    invitationsStudent: invitationCounts.student,
    invitationsDirector: invitationCounts.director,
    invitationsSuccess: invitationCounts.success,
    invitationsOffice: invitationCounts.office,
    invitationsMembers: invitationCounts.total,
  };

  return (
    <div className="min-h-screen bg-wpcanvas">
      <AdminBar
        unreadCount={unreadQuery.data?.unread ?? 0}
        showNotifications={showNotifications}
        onToggleSidebar={() => setSidebarOpen((open) => !open)}
      />
      <div className="relative flex">
        {user && (
          <Sidebar
            role={user.active_role}
            open={sidebarOpen}
            onNavigate={() => setSidebarOpen(false)}
            badges={badges}
            collapsed={collapsed}
            onToggleCollapse={toggleCollapsed}
          />
        )}
        <main className="min-w-0 flex-1 px-6 py-5">{children}</main>
      </div>
    </div>
  );
}

/**
 * 画面見出し（WPの「〜 <新規追加>」相当）。
 * 現在のURLから操作マニュアル（/help/:topicId）の該当ページを自動解決し、
 * 「使い方」リンクを添える（各ページ側で個別に指定する必要はない）。
 * マニュアルが自分のロール向けでない場合はリンク自体を出さない（死にリンクを防ぐ）。
 * パンくず（現在地までの道のり）も URL とロール別メニューから自動で出す（D-090）。
 */
export function PageHeader({
  title,
  description,
  actions,
  extraParents,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  /** URLから導けない段（例: イベント詳細の「分科会一覧」/ D-233） */
  extraParents?: Crumb[] | undefined;
}): ReactElement {
  const location = useLocation();
  const { user } = useAuth();
  const topicId = topicForPath(location.pathname);
  const topic = topicId !== null ? HELP_TOPICS.find((t) => t.id === topicId) : undefined;
  const showHelpLink =
    topic !== undefined &&
    user !== null &&
    topic.roles.includes(user.active_role) &&
    !location.pathname.startsWith('/help');
  const parents = [
    ...(user ? breadcrumbParents(user.active_role, location.pathname) : []),
    ...(extraParents ?? []),
  ];

  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        {parents.length > 0 && (
          <nav aria-label="現在地" className="mb-1.5 flex flex-wrap items-center gap-x-1.5 text-sm">
            {parents.map((crumb) => (
              <span key={crumb.to + crumb.label} className="flex items-center gap-x-1.5">
                <Link to={crumb.to} className="text-wpaccent hover:underline">
                  {crumb.label}
                </Link>
                <span aria-hidden="true" className="text-wptext-muted">
                  ›
                </span>
              </span>
            ))}
            <span aria-current="page" className="text-wptext-muted">
              {title}
            </span>
          </nav>
        )}
        <h1 className="text-2xl font-normal text-wptext-strong">{title}</h1>
        {description && <p className="mt-1.5 text-wptext-muted">{description}</p>}
        {showHelpLink && (
          <Link
            to={`/help/${topicId}`}
            className="mt-1.5 inline-flex items-center gap-1 text-wpaccent hover:underline"
          >
            <span aria-hidden="true">❓</span>使い方を見る
          </Link>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** ダッシュボードのウィジェットカード（指示書 §7-4）。 */
export function WidgetCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): ReactElement {
  return (
    <section className="rounded-sm border border-wpborder bg-white">
      <h2 className="border-b border-wpborder px-5 py-3 text-wppanel font-semibold text-wptext-strong">
        {title}
      </h2>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}
